import { useCallback, useState } from 'react';
import { isNil } from '@seekingalpha/samw-utils';
import { skipToken } from '@tanstack/react-query';

import { useQuery } from 'Services/query/useQuery';
import { useAuthorizedUser } from 'Services/User/useAuthorizedUser';
import { useToggleFollowUser } from 'Shared/hooks/useToggleFollowUser/useToggleFollowUser';
import { logger } from 'Utils/logger';
import {
  FUNCTIONS_MAP_BY_USER_TYPE,
  USER_TYPE,
} from 'Shared/hooks/useToggleFollowUser/utils';
import { WALL_STREET_BREAKFAST } from 'Constants/articleTypes';

import { useFollowings } from './useFollowings';

type ToggleFollowT = () => Promise<void>;

type UseUserFollowingButtonT = (arg1: {
  followingUserId?: string;
  isAuthor: boolean;
  slug?: string;
  onToggle?: (() => void) | null;
}) => {
  isFollowing: boolean;
  toggleFollow: ToggleFollowT;
  isLoading: boolean;
};

export const useUserFollowingButton: UseUserFollowingButtonT = ({
  followingUserId,
  isAuthor,
  onToggle,
  slug,
}) => {
  const userType =
    slug === WALL_STREET_BREAKFAST
      ? USER_TYPE.WallStreetBreakfast
      : USER_TYPE.User;
  const { isAuthorized } = useAuthorizedUser();

  const { followings, addFollowings, removeFollowings } = useFollowings();

  const { toggleFollow: toggleFollowUser } = useToggleFollowUser(userType);

  const [isPending, setIsPending] = useState(false);

  const isFollowing = followingUserId ? followings[followingUserId] : false;

  const shouldRequestFollowingState =
    isAuthorized && followingUserId && isNil(followings[followingUserId]);

  const { isPending: isFollowingPending } = useQuery({
    queryKey: ['following', followingUserId],
    queryFn: shouldRequestFollowingState
      ? async () => {
          const { data } = await FUNCTIONS_MAP_BY_USER_TYPE[
            userType
          ].getFollowings({
            userId: followingUserId,
          });

          const isFollowingFromResponse = data.length > 0;

          const update = isFollowingFromResponse
            ? addFollowings
            : removeFollowings;

          update(followingUserId);

          return isFollowingFromResponse;
        }
      : skipToken,
    gcTime: 0,
  });

  const toggleFollow: ToggleFollowT = useCallback(async () => {
    if (!followingUserId) {
      return;
    }

    setIsPending(true);

    try {
      await toggleFollowUser({
        isFollowing,
        userId: followingUserId,
        isAuthor,
      });

      onToggle?.();
    } catch (err: any) {
      logger.error(err);
    } finally {
      setIsPending(false);
    }
  }, [toggleFollowUser, isFollowing, followingUserId, isAuthor, onToggle]);

  return {
    isLoading: (shouldRequestFollowingState && isFollowingPending) || isPending,
    isFollowing,
    toggleFollow,
  };
};
